import { ReduxState } from '~/types/redux';
import { SweetenerKeys, Product, SelectedProductsState } from '../types/products';
import { CheckboxData } from '~/types/billing';

const { VRC, WIFI_SECURITY_FREE_TRIAL, WHOLE_HOME_WIFI_FREE_TRIAL } = SweetenerKeys;

export const isVrcSelected = (products: ReduxState['products'], internetProduct: Product): boolean => {
  return products?.planPageSelections?.selectedSweetener?.value !== 'skip' && !!internetProduct?.sweeteners?.[VRC];
};

export const isWifiSecureTrialActive = (
  customOffers: ReduxState['customOffers'],
  internetProduct: Product,
  selectedState: SelectedProductsState,
  selectWifiSecureFreeTrial?: boolean
): boolean => {
  return Boolean(
    (customOffers?.wifiSecureTrialEnabled &&
      internetProduct?.sweeteners?.[WIFI_SECURITY_FREE_TRIAL] &&
      Boolean(selectedState?.vas?.WIFI_SECURITY || customOffers?.includeWifiSecure)) ||
      selectWifiSecureFreeTrial ||
      false
  );
};

export const isWholeHomeWifiTrialActive = (
  customOffers: ReduxState['customOffers'],
  internetProduct: Product,
  selectedState: SelectedProductsState
): boolean => {
  return (
    (customOffers?.wholeHomeWifiTrialEnabled &&
      internetProduct?.sweeteners?.[WHOLE_HOME_WIFI_FREE_TRIAL] &&
      selectedState?.vas?.WHOLE_HOME_WIFI !== undefined) ??
    false
  );
};

export const getProductSpeed = (product: Product): string => {
  if (!Object.keys(product || {}).length) return '';

  // return key of any speed value that's TRUE
  const { is200, is500, is1Gig, is2Gig, is5Gig, is7Gig } = product;
  const speeds = { is200, is500, is1Gig, is2Gig, is5Gig, is7Gig };
  const selectedSpeed = Object.keys(speeds).find((key) => speeds[key]);
  return selectedSpeed;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const sortOrderByValue = (array: any[], sortArray: string[], key = 'name'): any[] => {
  return [...array]?.sort((a, b) => sortArray?.indexOf(a[key]) - sortArray?.indexOf(b[key]));
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getAddon = (displayName: string) => {
  const _name = displayName?.toLowerCase() || '';
  return {
    wifi: _name.includes('whole-home') && !_name.includes('plus'),
    wifiplus: _name.includes('wi-fi plus'),
    yttv: _name.includes('youtube'),
    eero: _name.includes('security') && !_name.includes('plus'),
    unbreakableWifi: _name.includes('unbreakable'),
    techpro: _name.includes('tech pro'),
    phone: _name.includes('phone') || _name.includes('voice'),
    homeshield: _name.includes('homeshield'),
    totalshield: _name.includes('total'),
    identityprotect: _name.includes('identity'),
    wifisecurityplus: _name.includes('security plus'),
    netflix: _name.includes('netflix'),
  };
};

// Finds a product in the selected state and returns that product OR undefined if it's not selected
export const getSelectedState = (selectedState: SelectedProductsState, product: Product) => {
  switch (product?.type) {
    case 'Secure':
      return selectedState?.secure?.[product.productId];
    case 'VAS':
      return selectedState?.vas?.[product.productKey];
    case 'Voice':
      return selectedState?.voice;
    case 'Video':
      return selectedState?.video?.[product.productId];
    case 'Broadband':
      return selectedState?.internet;
    default:
      return undefined;
  }
};

// Returns if a product is selected
export const getIsProductSelected = (selectedState: SelectedProductsState, product: Product): boolean => {
  return Boolean(getSelectedState(selectedState, product));
};

export const getPaperBillData = (amount: number): CheckboxData => {
  return {
    id: 'paper',
    label: `Paper bill${amount ? ` (+$${amount}/mo.)` : ''}`,
    text:
      amount > 0
        ? `I'd like to pay an extra $${amount} per month for a paper bill.`
        : "I'd like to receive a paper bill each month.",
  };
};

export const getPaperlessData = (amount: number): CheckboxData => {
  return {
    id: 'paperless',
    label: 'Paperless billing',
    text: amount > 0 ? `I'd like to save with paperless billing.` : null,
  };
};

export const hasVasSelected = (selectedProducts: SelectedProductsState): boolean => {
  const addOnTypes = ['voice', 'video', 'vas', 'secure'];
  const hasVas = addOnTypes.some((type) => {
    return selectedProducts?.[type] && Object.keys(selectedProducts[type]).length > 0;
  });
  return hasVas;
};

export const getTooltipFreeTrialAddOn = (addOn: string): string => {
  const tooltips: { [key: string]: string } = {
    [WIFI_SECURITY_FREE_TRIAL]:
      'After the promotional period, Wi-Fi Security service will be billed at the current price of $5/mo. Cancel anytime via Frontier Gigachat. No prorated refunds will be available. Frontier Fiber Internet subscription with eero router required.',
    [WHOLE_HOME_WIFI_FREE_TRIAL]:
      'After the promotional period, Whole-Home Wi-Fi service will be billed at the current price of $10/mo. Cancel anytime via Frontier Gigachat. No prorated refunds will be available.',
  };

  return tooltips[addOn];
};
